import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { ArrowRight, Sparkles, FileText } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api';
const ITEMS_PER_PAGE = 6;

const LiveExhibitionPage = () => {
  const [artisans, setArtisans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/live-exhibition`);
        const json = await res.json();
        if (json.success) setArtisans(json.data || []);
      } catch (err) {
        console.error('Failed to load live exhibition:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const totalPages = Math.ceil(artisans.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentArtisans = artisans.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleCardClick = (artisan) => {
    if (artisan.pdf_url) {
      window.open(artisan.pdf_url, '_blank', 'noopener,noreferrer');
    }
  };

  const getPaginationNumbers = () => {
    const numbers = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) numbers.push(i);
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 3; i++) numbers.push(i);
      numbers.push('...');
      numbers.push(totalPages);
    } else if (currentPage >= totalPages - 2) {
      numbers.push(1);
      numbers.push('...');
      for (let i = totalPages - 2; i <= totalPages; i++) numbers.push(i);
    } else {
      numbers.push(1);
      numbers.push('...');
      for (let i = currentPage - 1; i <= currentPage + 1; i++) numbers.push(i);
      numbers.push('...');
      numbers.push(totalPages);
    }
    return numbers;
  };

  return (
    <>
      <Helmet>
        <title>Live Exhibition (Demonstrative Area) - SACIT Symposium</title>
        <meta name="description" content="นิทรรศการสด: พื้นที่สาธิตการทำงานศิลปหัตถกรรมไทย SACIT Symposium 2025" />
      </Helmet>

      <div className="min-h-screen">
        {/* Hero Banner */}
        <motion.div
          className="w-full flex items-center justify-center relative"
          style={{ height: '280px', background: 'linear-gradient(90deg, #BFB4EE 0%, #B3FFD1 100%)' }}
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <motion.h1
            className="relative z-10 text-center px-4"
            style={{
              fontFamily: 'Prompt',
              fontWeight: 700,
              fontSize: 'clamp(22px, 3.5vw, 40px)',
              color: '#533193',
              marginTop: '60px',
            }}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
          >
            Live Exhibition (Demonstrative Area)
          </motion.h1>
          <motion.div className="absolute top-10 right-10"
            animate={{ rotate: 360, scale: [1, 1.2, 1] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}>
            <Sparkles className="w-6 h-6 text-white/60" />
          </motion.div>
          <motion.div className="absolute bottom-10 left-10"
            animate={{ rotate: -360, scale: [1, 1.1, 1] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}>
            <Sparkles className="w-4 h-4 text-white/40" />
          </motion.div>
        </motion.div>

        {/* Sub Navigation Bar */}
        <motion.div
          className="w-full py-4 shadow-md border-b-4"
          style={{ background: 'linear-gradient(90deg, #533193 0%, #BFB4EE 100%)', borderBottomColor: '#533193' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-center gap-6 flex-wrap">
              {[
                { to: '/news', label: 'ข่าวสาร' },
                { to: '/exhibition', label: 'นิทรรศการ' },
                { to: '/creative-works', label: 'ผลงานสร้างสรรค์' },
                { to: '/images', label: 'ภาพ' },
                { to: '/videos', label: 'วิดีโอ' },
                { to: '/proceeding', label: 'Proceeding' },
                { to: '/academic-articles', label: 'บทความวิชาการ' },
              ].map((item) => (
                <Link key={item.to} to={item.to} className="cursor-pointer">
                  <span style={{
                    background: 'var(--gra-1, linear-gradient(90deg, #BFB4EE 0%, #B3FFD1 100%))',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontFamily: 'Prompt',
                  }}>
                    {item.label}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Sub-heading bar */}
        <motion.div
          className="w-full py-3"
          style={{ background: 'linear-gradient(90deg, #533193 0%, #BFB4EE 100%)' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <div className="container mx-auto flex justify-center items-center">
            <motion.span
              className="flex items-center gap-2 drop-shadow"
              style={{
                fontFamily: 'Prompt',
                fontSize: '18px',
                fontWeight: 500,
                background: 'linear-gradient(90deg, #BFB4EE 0%, #B3FFD1 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              <motion.svg width="16" height="16" viewBox="0 0 18 18" fill="none"
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
                <polygon points="9,2 16,9 9,16 2,9" fill="#C7BFFF" />
              </motion.svg>
              พื้นที่สาธิตการทำงานศิลปหัตถกรรมไทย
            </motion.span>
          </div>
        </motion.div>

        {/* Cards Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Loading skeleton */}
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-gray-200 rounded-lg animate-pulse" style={{ height: '500px' }} />
              ))}
            </div>
          )}

          {/* Empty state */}
          {!loading && artisans.length === 0 && (
            <motion.div className="text-center py-20"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="text-6xl mb-4">🎨</div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2" style={{ fontFamily: 'Prompt' }}>
                ยังไม่มีข้อมูลช่างฝีมือ
              </h3>
              <p className="text-gray-500" style={{ fontFamily: 'Prompt' }}>
                ข้อมูลจะปรากฏที่นี่เมื่อมีการเพิ่มข้อมูล
              </p>
            </motion.div>
          )}

          {/* Artisans Grid */}
          <AnimatePresence mode="wait">
            {!loading && artisans.length > 0 && (
              <motion.div
                key={currentPage}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
              >
                {currentArtisans.map((artisan, index) => (
                  <motion.div
                    key={artisan.id}
                    className="bg-white rounded-lg shadow-lg overflow-hidden relative cursor-pointer"
                    style={{ height: '500px' }}
                    initial={{ opacity: 0, y: 50, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.6, delay: index * 0.1, ease: 'easeOut' }}
                    whileHover={{ scale: 1.05, y: -10, transition: { duration: 0.3 } }}
                    onHoverStart={() => setHoveredCard(artisan.id)}
                    onHoverEnd={() => setHoveredCard(null)}
                    onClick={() => handleCardClick(artisan)}
                  >
                    <div className="relative w-full h-full">
                      {artisan.photo_url ? (
                        <motion.img
                          src={artisan.photo_url}
                          alt={artisan.name}
                          className="w-full h-full object-cover"
                          whileHover={{ scale: 1.08 }}
                          transition={{ duration: 0.5 }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-purple-100 to-green-100 flex items-center justify-center">
                          <span className="text-6xl">🎨</span>
                        </div>
                      )}

                      {/* PDF badge */}
                      {artisan.pdf_url && (
                        <div className="absolute top-4 left-4">
                          <span className="flex items-center gap-1 px-2 py-1 bg-white/90 backdrop-blur-sm rounded text-xs font-medium"
                            style={{ color: '#533193', fontFamily: 'Prompt' }}>
                            <FileText className="w-3 h-3" />
                            PDF
                          </span>
                        </div>
                      )}

                      {/* Bottom overlay */}
                      <motion.div
                        className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 + 0.2 }}
                      >
                        <div>
                          {artisan.technique && (
                            <motion.p className="text-gray-300 text-sm mb-1"
                              style={{ fontFamily: 'Prompt' }}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.3, delay: index * 0.1 + 0.4 }}>
                              {artisan.technique}
                            </motion.p>
                          )}
                          <motion.h3 className="text-xl font-bold text-white mb-1"
                            style={{ fontFamily: 'Prompt' }}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.1 + 0.5 }}>
                            {artisan.name}
                          </motion.h3>
                          {artisan.role && (
                            <motion.p className="text-gray-300 text-sm"
                              style={{ fontFamily: 'Prompt' }}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.3, delay: index * 0.1 + 0.6 }}>
                              {artisan.role}
                            </motion.p>
                          )}
                        </div>

                        {artisan.pdf_url && (
                          <motion.div className="flex justify-end mt-4"
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3, delay: index * 0.1 + 0.7 }}>
                            <motion.button
                              className="w-12 h-12 bg-purple-600 hover:bg-purple-700 rounded-full flex items-center justify-center shadow-lg"
                              title={`คลิกเพื่อดูเอกสาร ${artisan.name}`}
                              whileHover={{ scale: 1.2, rotate: 15, transition: { duration: 0.2 } }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <ArrowRight className="w-5 h-5 text-white" />
                            </motion.button>
                          </motion.div>
                        )}
                      </motion.div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <motion.div
              className="flex justify-center items-center space-x-2 mt-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.8 }}
            >
              {getPaginationNumbers().map((number, index) => (
                <motion.button
                  key={index}
                  onClick={() => typeof number === 'number' && setCurrentPage(number)}
                  disabled={typeof number !== 'number'}
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors duration-200 ${
                    typeof number === 'number'
                      ? currentPage === number
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      : 'text-gray-400 cursor-default'
                  }`}
                  whileHover={{ scale: typeof number === 'number' ? 1.1 : 1 }}
                  whileTap={{ scale: 0.9 }}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  {number}
                </motion.button>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </>
  );
};

export default LiveExhibitionPage;
